// PATH: src/components/coverage/coverage.ts
/**
 * Couverture d'un programme : logique pure de la page « Couverture ».
 * Les lignes viennent telles quelles de `GET /v1/coverage` : une par offre du
 * catalogue (matière × niveau × filière), plus les cases hors catalogue qui
 * ont une ossature ou des chapitres. L'ordre des étapes, niveaux et matières
 * vient du catalogue. Aucune matière, aucun niveau, aucune filière n'est
 * connu du code.
 *
 * Le dénominateur de tout taux est le CATALOGUE, jamais l'ossature : rapporté
 * à l'ossature, une offre sans ossature disparaissait (l'anglais du collège,
 * offert en C1-C3 sans ossature, laissait le collège « couvert à 100 % » le
 * 19/10).
 */
import type { CoverageItem, OfferState, OssatureChapterItem } from "../../api/coverage/coverage";
import type { CurriculumProgram } from "../../models/domain";

/** États d'une offre, du meilleur au pire (ordre de la légende et des barres). */
export const OFFER_STATES: OfferState[] = ["couverte", "partielle", "a_rediger", "ossature_manquante"];

/**
 * Avancement d'une offre, 0..1 : chapitres de son ossature jouables sur
 * chapitres prévus. Sans ossature, 0 : rien n'est prévu, donc rien n'est
 * couvert.
 */
export const offerProgress = (it: Pick<CoverageItem, "ossature_chapters" | "ossature_playable">): number =>
  it.ossature_chapters > 0 ? Math.min(1, it.ossature_playable / it.ossature_chapters) : 0;

/** Comptes d'un ensemble de lignes : offres du catalogue seulement. */
export interface CatalogTotals {
  /** Offres du catalogue (dénominateur). */
  offers: number;
  byState: Record<OfferState, number>;
  /** Somme des avancements des offres (taux = progress / offers). */
  progress: number;
  /** Chapitres de l'ossature des offres : prévus, importés, jouables. */
  ossature_chapters: number;
  ossature_imported: number;
  ossature_playable: number;
  /** Cases hors catalogue rencontrées (montrées, jamais comptées). */
  outside: number;
}

const emptyByState = (): Record<OfferState, number> => ({
  couverte: 0,
  partielle: 0,
  a_rediger: 0,
  ossature_manquante: 0,
});

export const catalogTotals = (items: CoverageItem[]): CatalogTotals =>
  items.reduce<CatalogTotals>(
    (acc, it) => {
      if (!it.in_catalog) return { ...acc, outside: acc.outside + 1 };
      return {
        offers: acc.offers + 1,
        byState: { ...acc.byState, [it.state]: acc.byState[it.state] + 1 },
        progress: acc.progress + offerProgress(it),
        ossature_chapters: acc.ossature_chapters + it.ossature_chapters,
        ossature_imported: acc.ossature_imported + it.ossature_imported,
        ossature_playable: acc.ossature_playable + it.ossature_playable,
        outside: acc.outside,
      };
    },
    {
      offers: 0,
      byState: emptyByState(),
      progress: 0,
      ossature_chapters: 0,
      ossature_imported: 0,
      ossature_playable: 0,
      outside: 0,
    },
  );

/**
 * Taux de couverture, 0..1 : moyenne des avancements des offres. Chaque offre
 * pèse autant : une offre sans ossature compte pour 0, et une matière à 40
 * chapitres n'écrase pas une matière à 2.
 */
export const catalogRate = (t: Pick<CatalogTotals, "offers" | "progress">): number =>
  t.offers > 0 ? t.progress / t.offers : 0;

/** « 12 % », « 0,4 % », « 100 % » : une décimale sous 10 %, sans « 0 % » ni « 100 % » trompeurs. */
export const formatPercent = (ratio: number): string => {
  const pct = ratio * 100;
  if (pct === 0) return "0 %";
  if (pct < 10) return `${pct.toLocaleString("fr-FR", { maximumFractionDigits: 1, minimumFractionDigits: pct < 1 ? 1 : 0 })} %`;
  return `${Math.floor(pct)} %`;
};

/** Libellé de filière : son code, ou « Tronc commun » quand la ligne n'en a pas. */
export const coverageTrackLabel = (item: Pick<CoverageItem, "track_id" | "track_code">): string =>
  item.track_id ? item.track_code || item.track_id : "Tronc commun";

// ---------------------------------------------------------------------------
// Matrice matière × niveau
// ---------------------------------------------------------------------------

export interface MatrixGrade {
  id: string;
  code: string;
  name: string;
}

export interface MatrixStage {
  id: string;
  name: string;
  grades: MatrixGrade[];
}

export interface MatrixSubject {
  id: string;
  code: string;
  name: string;
}

/** Une case : toutes les filières d'une matière à un niveau. */
export interface MatrixCell {
  subjectId: string;
  gradeId: string;
  /** Les lignes de la case, une par filière (ou une seule, tronc commun). */
  rows: CoverageItem[];
}

/** État d'une case : celui de ses offres, ou `hors_catalogue` si elle n'en a aucune. */
export type CellState = OfferState | "hors_catalogue";

/**
 * État d'une case à plusieurs filières : identique pour toutes → cet état ;
 * sinon `partielle` dès qu'une offre avance, `a_rediger` sinon (mélange
 * d'offres à rédiger et sans ossature). Le nombre d'offres sans ossature est
 * dit à part (`cellMissing`) pour ne jamais être masqué par le mélange.
 */
export const cellState = (cell: Pick<MatrixCell, "rows">): CellState => {
  const states = cell.rows.filter((r) => r.in_catalog).map((r) => r.state);
  if (!states.length) return "hors_catalogue";
  if (states.every((s) => s === states[0])) return states[0];
  if (states.some((s) => s === "couverte" || s === "partielle")) return "partielle";
  return "a_rediger";
};

/** Offres de la case sans ossature. */
export const cellMissing = (cell: Pick<MatrixCell, "rows">): number =>
  cell.rows.filter((r) => r.in_catalog && r.state === "ossature_manquante").length;

/** Chapitres de toutes les lignes d'une case (hors catalogue compris) : ce que montre son tiroir. */
export const cellChapters = (cell: Pick<MatrixCell, "rows">) =>
  cell.rows.reduce(
    (acc, r) => ({
      ossature_chapters: acc.ossature_chapters + r.ossature_chapters,
      ossature_imported: acc.ossature_imported + r.ossature_imported,
      ossature_playable: acc.ossature_playable + r.ossature_playable,
      imported: acc.imported + r.imported,
      playable: acc.playable + r.playable,
    }),
    { ossature_chapters: 0, ossature_imported: 0, ossature_playable: 0, imported: 0, playable: 0 },
  );

export interface CoverageMatrix {
  stages: MatrixStage[];
  subjects: MatrixSubject[];
  cells: Map<string, MatrixCell>;
}

export const cellKey = (subjectId: string, gradeId: string) => `${subjectId}|${gradeId}`;

/**
 * Construit la matrice. Les niveaux suivent l'ordre du catalogue (étapes puis
 * niveaux) ; un niveau sans aucune ligne n'est pas montré. Les matières
 * suivent leur ordre au catalogue, puis leur nom. Sans catalogue, l'ordre du
 * serveur fait foi et tout tombe dans une étape sans nom.
 */
export const buildCoverageMatrix = (
  items: CoverageItem[],
  program?: CurriculumProgram | null,
): CoverageMatrix => {
  const cells = new Map<string, MatrixCell>();
  const gradesWithRows = new Set<string>();
  const subjectsSeen = new Map<string, MatrixSubject>();

  items.forEach((it) => {
    gradesWithRows.add(it.grade_id);
    if (!subjectsSeen.has(it.subject_id)) {
      subjectsSeen.set(it.subject_id, { id: it.subject_id, code: it.subject_code, name: it.subject_name || it.subject_code });
    }
    const key = cellKey(it.subject_id, it.grade_id);
    const prev = cells.get(key);
    cells.set(key, { subjectId: it.subject_id, gradeId: it.grade_id, rows: [...(prev?.rows ?? []), it] });
  });

  // Étapes et niveaux : catalogue d'abord, puis ce que le catalogue ignore.
  const stages: MatrixStage[] = [];
  const placed = new Set<string>();
  [...(program?.stages ?? [])]
    .sort((a, b) => (a.stage.sort_order ?? 0) - (b.stage.sort_order ?? 0))
    .forEach((st) => {
      const grades = [...st.grades]
        .sort((a, b) => (a.grade.sort_order ?? 0) - (b.grade.sort_order ?? 0))
        .filter((g) => gradesWithRows.has(g.grade.id))
        .map((g) => ({ id: g.grade.id, code: g.grade.code, name: g.grade.name }));
      grades.forEach((g) => placed.add(g.id));
      if (grades.length) stages.push({ id: st.stage.id, name: st.stage.name, grades });
    });
  const orphans: MatrixGrade[] = [];
  items.forEach((it) => {
    if (placed.has(it.grade_id)) return;
    placed.add(it.grade_id);
    orphans.push({ id: it.grade_id, code: it.grade_code, name: it.grade_name || it.grade_code });
  });
  if (orphans.length) stages.push({ id: "", name: stages.length ? "Autres" : "", grades: orphans });

  const subjectOrder = new Map<string, number>();
  (program?.subjects ?? []).forEach((s) => subjectOrder.set(s.id, s.sort_order ?? 0));
  const subjects = Array.from(subjectsSeen.values()).sort(
    (a, b) =>
      (subjectOrder.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (subjectOrder.get(b.id) ?? Number.MAX_SAFE_INTEGER) ||
      a.name.localeCompare(b.name, "fr"),
  );

  return { stages, subjects, cells };
};

/**
 * Leçons des chapitres importés d'une case (toutes filières) et leçons prêtes.
 * `lessons` à 0 : rien d'importé, ou serveur qui ne les compte pas encore.
 */
export const cellLessons = (cell: Pick<MatrixCell, "rows">): { lessons: number; ready: number } =>
  cell.rows.reduce(
    (acc, r) => ({ lessons: acc.lessons + (r.lessons ?? 0), ready: acc.ready + (r.lessons_ready ?? 0) }),
    { lessons: 0, ready: 0 },
  );

/** Lignes des cases visibles d'une sélection de niveaux (et d'une matière). */
const rowsOf = (matrix: CoverageMatrix, gradeIds: string[], subjectId?: string): CoverageItem[] => {
  const grades = new Set(gradeIds);
  return Array.from(matrix.cells.values())
    .filter((c) => grades.has(c.gradeId) && (!subjectId || c.subjectId === subjectId))
    .flatMap((c) => c.rows);
};

/** Comptes d'une étape entière. */
export const stageTotals = (matrix: CoverageMatrix, stage: MatrixStage): CatalogTotals =>
  catalogTotals(rowsOf(matrix, stage.grades.map((g) => g.id)));

/** Comptes de niveaux (une colonne, ou tout le tableau), toutes matières. */
export const gradesTotals = (matrix: CoverageMatrix, gradeIds: string[]): CatalogTotals =>
  catalogTotals(rowsOf(matrix, gradeIds));

/** Comptes d'une matière (ligne), sur les niveaux visibles. */
export const subjectTotals = (matrix: CoverageMatrix, subjectId: string, gradeIds: string[]): CatalogTotals =>
  catalogTotals(rowsOf(matrix, gradeIds, subjectId));

/** Offres sans ossature, groupées par matière dans l'ordre de la matrice. */
export const missingOssatureBySubject = (
  matrix: CoverageMatrix,
  gradeIds: string[],
): { subject: MatrixSubject; rows: CoverageItem[] }[] => {
  const order = new Map(gradeIds.map((g, i) => [g, i] as const));
  return matrix.subjects
    .map((subject) => ({
      subject,
      rows: rowsOf(matrix, gradeIds, subject.id)
        .filter((r) => r.in_catalog && r.state === "ossature_manquante")
        .sort(
          (a, b) =>
            (order.get(a.grade_id) ?? 0) - (order.get(b.grade_id) ?? 0) ||
            (a.track_code ?? "").localeCompare(b.track_code ?? ""),
        ),
    }))
    .filter((x) => x.rows.length > 0);
};

// ---------------------------------------------------------------------------
// Chapitres d'une case
// ---------------------------------------------------------------------------

export type ChapterState = "todo" | "inactive" | "no_objectives" | "no_facts" | "playable" | "extra";

/**
 * État d'un chapitre pour l'éditeur :
 * - `todo`     : prévu par l'ossature, pas encore importé ;
 * - `inactive` : importé mais masqué aux élèves ;
 * - `no_objectives` : importé, actif, mais aucun objectif extrait ;
 * - `no_facts` : importé, actif, des objectifs mais aucun n'a de faits ;
 * - `playable` : jouable ;
 * - `extra`    : importé hors ossature (et non jouable).
 */
export const chapterState = (c: OssatureChapterItem): ChapterState => {
  if (!c.document_id) return "todo";
  if (c.playable) return "playable";
  if (!c.in_ossature) return "extra";
  if (c.is_active === false) return "inactive";
  if (c.objectives === 0) return "no_objectives";
  return "no_facts";
};

/** Rang lisible d'un chapitre : « S1 · 3 », « 3 », « S2 », ou « — ». */
export const chapterRank = (c: Pick<OssatureChapterItem, "semester" | "chapter_order">): string => {
  const parts: string[] = [];
  if (c.semester != null) parts.push(`S${c.semester}`);
  if (c.chapter_order != null) parts.push(String(c.chapter_order));
  return parts.length ? parts.join(" · ") : "—";
};

// ---------------------------------------------------------------------------
// Relecture humaine
// ---------------------------------------------------------------------------

export interface ReviewTotals {
  toReview: number;
  toFix: number;
  validated: number;
  /** Le serveur compte la relecture (sinon, ne rien afficher). */
  measured: boolean;
}

/** Chapitres à relire, à corriger, validés, sur les lignes de couverture. */
export const reviewTotals = (
  items: Pick<CoverageItem, "to_review" | "to_fix" | "review_validated">[],
): ReviewTotals =>
  items.reduce<ReviewTotals>(
    (acc, it) => ({
      toReview: acc.toReview + (it.to_review ?? 0),
      toFix: acc.toFix + (it.to_fix ?? 0),
      validated: acc.validated + (it.review_validated ?? 0),
      measured: acc.measured || it.to_review != null,
    }),
    { toReview: 0, toFix: 0, validated: 0, measured: false },
  );
